export type ProductReference = {
  productId: string;
  partNumber?: string | null;
};

/** Read both legacy single-product cards and current lists without image snapshots. */
export function productReferences(data: unknown): ProductReference[] {
  if (!data || typeof data !== "object") return [];
  const card = data as Record<string, unknown>;
  const entries = Array.isArray(card.products) ? card.products : [card];
  return entries.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const item = entry as Record<string, unknown>;
    const id = item.productId || item.id;
    if (typeof id !== "string" || !id.trim()) return [];
    return [
      {
        productId: id.trim(),
        partNumber:
          typeof item.partNumber === "string" ? item.partNumber : null,
      },
    ];
  });
}

/** Strip only the obsolete snapshot; keep all other block metadata intact. */
export function withoutProductImages(data: Record<string, unknown>) {
  const { heroImage: _image, ...rest } = data;
  return {
    ...rest,
    ...(Array.isArray(rest.products)
      ? {
          products: rest.products.map((item: unknown) => {
            if (!item || typeof item !== "object" || Array.isArray(item))
              return item;
            const entry = item as Record<string, unknown>;
            const { heroImage: _image, ...reference } = entry;
            return reference;
          }),
        }
      : {}),
  };
}

export function stripProductCardSnapshots<T>(value: T): T {
  if (Array.isArray(value)) return value.map(stripProductCardSnapshots) as T;
  if (!value || typeof value !== "object") return value;
  const node = Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      stripProductCardSnapshots(child),
    ]),
  );
  if (
    node.type === "productCard" &&
    node.data &&
    typeof node.data === "object" &&
    !Array.isArray(node.data)
  ) {
    node.data = withoutProductImages(node.data);
  }
  return node as T;
}
